import Link from "next/link";
import Head from "next/head";
import classNames from "classnames";
import { serialize } from "next-mdx-remote/serialize";
import { MDXRemote } from "next-mdx-remote";

import { imageUrlFor } from "lib/remoteContent";
import ApplicationLayout from "components/ApplicationLayout";
import Navbar from "components/Navbar";
import PageContent from "components/PageContent";
import ReadyForTakeOff from "components/ReadyForTakeOff";
import SocialFooter from "components/SocialFooter";
import SharedFooter from "components/SharedFooter";
import StoriesWrapper from "components/community-stories/StoriesWrapper";
import StoryCard from "components/community-stories/StoryCard";
import AddStory from "components/community-stories/AddStory";
import UserAvatar from "components/community-stories/UserAvatar";
import ChevronIcon from "assets/icons/chevron-up.svg";
import TwitterIcon from "assets/icons/social-twitter.svg";
import GlobeIcon from "assets/icons/globe.svg";
import RaycastLogoNegIcon from "assets/icons/raycast-logo-neg.svg";
import SlackIcon from "assets/icons/social-slack.svg";

import Container from "components/Container";

import { getAllCommunityStories } from "lib/remoteContent";

import styles from "./[slug].module.css";

import type { GetStaticProps, GetStaticPaths } from "next";
import type { MDXRemoteSerializeResult } from "next-mdx-remote";
import type { Page } from "types/page";
import type { CommunityStory } from "lib/remoteContent";

type PageProps = {
  story: CommunityStory;
  moreStories: CommunityStory[];
  mdxSource: MDXRemoteSerializeResult | null;
};

type PageParams = {
  slug: string;
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = (await getAllCommunityStories()).map(({ slug }) => ({
    params: {
      slug: slug.current,
    },
  }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<PageProps, PageParams> = async (context) => {
  if (context.params === undefined) {
    return {
      notFound: true,
    };
  }

  const { slug } = context.params;
  const stories = await getAllCommunityStories();
  const story = stories.find((s) => s.slug.current === slug);
  if (story !== undefined) {
    const storyIndex = stories.findIndex((s) => s.slug.current === slug);
    const moreStories = [...stories, ...stories].slice(storyIndex + 1, storyIndex + 4);
    const mdxSource = story.content ? await serialize(story.content) : null;
    return {
      props: {
        story,
        moreStories,
        mdxSource,
      },
    };
  }

  return {
    notFound: true,
  };
};

const SocialLink = ({ link, icon, children }: { link: string; icon: React.ReactElement; children: string }) => (
  <div className={styles.social}>
    {icon}{" "}
    <a href={link} target="__blank">
      {children}
    </a>
  </div>
);

const CommunityStory: Page<PageProps> = ({ story, moreStories, mdxSource }: PageProps) => {
  const { name, slug, position, avatar, bio, twitter, raycast, link, slack } = story;
  return (
    <>
      <Head>
        <title>
          Raycast Community Stories: {name}, {position}
        </title>
        <meta
          property="og:image"
          content={`https://${process.env.NEXT_PUBLIC_VERCEL_URL}/community-stories/og-${slug.current}.png`}
          key="og-image"
        />
        <meta property="og:title" content={`Raycast Community Stories: ${name}, ${position}`} />
        <meta property="og:description" content={bio} />
        <meta property="og:image:width" content="1024" />
        <meta property="og:image:height" content="512" />
        <meta property="twitter:card" content="summary_large_image" />
      </Head>
      <div className={styles.storyWrapper}>
        <div className={styles.backLinkWrapper}>
          <Link href="/community-stories">
            <a className={styles.backLink}>
              <ChevronIcon className={classNames("icon", styles.chevron)} />
              &nbsp;Go back
            </a>
          </Link>
        </div>
        <div className={styles.cols}>
          <div className={styles.member}>
            <div className={styles.memberDetails}>
              <UserAvatar avatar={imageUrlFor(avatar).width(160).url()} alt={name} size="big" />
              <div className={styles.name}>{name}</div>
            </div>
            {bio ? <div className={styles.bio}>{bio}</div> : null}
            {raycast ? (
              <SocialLink icon={<RaycastLogoNegIcon className="icon" />} link={`/${raycast}`}>
                {`@${raycast}`}
              </SocialLink>
            ) : null}
            {twitter ? (
              <SocialLink icon={<TwitterIcon className="icon" />} link={`https://twitter.com/${twitter}`}>
                {twitter}
              </SocialLink>
            ) : null}
            {link ? (
              <SocialLink icon={<GlobeIcon className="icon" />} link={`http://${link}`}>
                {link}
              </SocialLink>
            ) : null}
            {slack ? (
              <SocialLink
                icon={<SlackIcon className="icon" />}
                link={`https://raycastcommunity.slack.com/app_redirect?channel=${slack}`}
              >
                {slack}
              </SocialLink>
            ) : null}
          </div>
          {mdxSource ? (
            <div className={styles.post}>
              <MDXRemote {...mdxSource} />
            </div>
          ) : null}
        </div>
        <h2 className={styles.subtitle}>More stories</h2>
        <StoriesWrapper>
          {moreStories.map((story) => (
            <StoryCard story={story} key={story.slug.current} />
          ))}
          <AddStory />
        </StoriesWrapper>
      </div>
    </>
  );
};

CommunityStory.getLayout = (page) => {
  return (
    <ApplicationLayout>
      <Navbar />
      <PageContent>
        <Container>
          {page}
          <ReadyForTakeOff />
          <SocialFooter />
        </Container>
      </PageContent>
      <SharedFooter />
    </ApplicationLayout>
  );
};

export default CommunityStory;
